import React from "react";
import { Link } from "react-router-dom";
import { FaCode, FaPen, FaGithub, FaExternalLinkAlt } from "react-icons/fa";
import { formatRelativeDate } from "../utils/dates";
import RevealOnScroll from "./RevealOnScroll";

const BuildCard = ({ icon, label, title, meta, href, external }) => {
  const cardClass =
    "group flex flex-col h-full p-5 rounded-lg border border-gray-200 dark:border-brand-purple/20 bg-gradient-to-br from-gray-50 to-white dark:from-brand-darker dark:to-[#2a2a2a] hover:border-brand-gold/40 hover:shadow-glow transition-all duration-300";

  const inner = (
    <>
      <div className="flex items-center gap-2 text-brand-gold text-sm font-semibold mb-3">
        {icon}
        <span>{label}</span>
      </div>
      <h4 className="text-lg font-bold text-gray-800 dark:text-brand-cream mb-2 group-hover:text-brand-gold transition-colors line-clamp-2">
        {title}
      </h4>
      {meta && (
        <p className="text-sm text-gray-600 dark:text-brand-cream/70 mt-auto">
          {meta}
        </p>
      )}
    </>
  );

  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cardClass}
      >
        {inner}
        <FaExternalLinkAlt className="w-3 h-3 text-gray-400 group-hover:text-brand-gold mt-3" />
      </a>
    );
  }

  return (
    <Link to={href} className={cardClass}>
      {inner}
    </Link>
  );
};

const RecentBuilds = ({ activity, loading }) => {
  if (loading) {
    return (
      <div className="mb-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-36 rounded-lg bg-gray-100 dark:bg-brand-darker animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (!activity) return null;

  const cards = [];
  const featuredId = activity.featuredProject?.id;

  if (
    activity.latestProject &&
    activity.latestProject.id !== featuredId
  ) {
    cards.push({
      key: "project",
      icon: <FaCode />,
      label: "Latest Project",
      title: activity.latestProject.title,
      meta: formatRelativeDate(activity.latestProject.updatedAt),
      href: "/projects",
    });
  }

  if (activity.latestBlog) {
    cards.push({
      key: "blog",
      icon: <FaPen />,
      label: "Latest Blog",
      title: activity.latestBlog.title,
      meta: formatRelativeDate(activity.latestBlog.updatedAt),
      href: `/blogs/${activity.latestBlog.id}`,
    });
  }

  if (activity.githubRepo) {
    cards.push({
      key: "github",
      icon: <FaGithub />,
      label: "GitHub Activity",
      title: activity.githubRepo.name,
      meta: activity.githubRepo.language
        ? `${activity.githubRepo.language} · ${formatRelativeDate(activity.githubRepo.updatedAt)}`
        : formatRelativeDate(activity.githubRepo.updatedAt),
      href: activity.githubRepo.url,
      external: true,
    });
  }

  if (cards.length === 0) return null;

  return (
    <RevealOnScroll className="mb-12">
      <h3 className="text-2xl md:text-3xl font-bold text-gradient text-center mb-6">
        Recent Builds
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((card) => (
          <BuildCard key={card.key} {...card} />
        ))}
      </div>
    </RevealOnScroll>
  );
};

export default RecentBuilds;
